'use client';
// Référentiel des critères de performance (lot V0) — ISO 15189:2022 §7.3.3.
//
// L'écran existe pour une raison simple : tant que les limites d'acceptabilité
// ne sont pas EN BASE, avec leur source et leur version, tout verdict de
// conformité rendu par la plateforme serait rendu contre un nombre écrit dans
// le code. Ce n'est pas un tableau de consultation, c'est la pièce que
// l'auditeur demandera quand il verra « conforme » sur un dossier de méthode.
//
// Deux choses que cet écran refuse de faire :
//
//  · Combler une cellule vide. La table a été transcrite depuis un document
//    imprimé ; ce qui n'a pas pu être lu reste vide et se voit comme tel. Une
//    valeur « à peu près » produirait un seuil crédible et faux.
//
//  · Laisser modifier la version d'une source standard. Un dossier figé cite
//    « SFBC, version X » : réécrire la version sous ses pieds rendrait sa
//    citation fausse sans que rien ne le signale.
import React, { useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';
import { useRemonteEnHaut, Skeleton,
  I, card, inputStyle, EmptyState, FilterBar, RefISO, useBusy,
  usePagination, Pagination } from '@/components/ui';
import { ligneOuvrable, SansOuvrir, enTeteCollant } from '@/shared/Tableau';
import { H1, lbl } from './ressources/commun';
import { RegistresLies } from './manuel/RegistresLies';
import { SuppressionRaisonnee, type Consequence } from '@/components/SuppressionRaisonnee';
import { useFormulaire } from '@/components/Formulaire';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
type Ligne = {
  documentId: string; libelle: string; analyteCode: string | null; famille: string;
  unite: string | null; domaine: string | null; valeursUsuelles: string | null;
  intervallePct: number | null;
  niveauBas: number | null; niveauMoyen: number | null; niveauEleve: number | null;
  sourceId: string | null; sourceCode: string | null; sourceVersion: string | null;
  actif: boolean;
} & Record<string, number | null | string | boolean>;

type Source = {
  documentId: string; code: string; libelle: string; version: string;
  referenceBiblio: string | null; dateVersion: string | null; commentaire: string | null;
  standard: boolean; actif: boolean; nbLignes: number;
};

type Dossier = {
  sources: Source[]; lignes: Ligne[];
  familles: string[]; champsCriteres: string[];
  compte: {
    sources: number; lignes: number;
    parFamille: { famille: string; n: number }[];
    cellulesVides: number; cellulesTotal: number;
  };
  peutModifier: boolean;
};

const FAMILLES: Record<string, string> = {
  plasmatique: 'Dosages plasmatiques', urinaire: 'Dosages urinaires',
  enzyme: 'Enzymes', immunochimie: 'Immunochimie', autre: 'Autres',
};
/** Les six familles de critères de la table, dans l'ordre du gabarit. */
const CRITERES: { cle: string; label: string; unite: string; aide: string }[] = [
  { cle: 'repetabilite', label: 'Répétabilité', unite: 'CV %', aide: 'Dispersion en conditions strictement identiques — c’est le plancher : aucune autre performance ne peut être meilleure.' },
  { cle: 'reproductibilite', label: 'Reproductibilité', unite: 'CV %', aide: 'Dispersion en conditions variées dans le temps. C’est elle que compare la fidélité intermédiaire du dossier de méthode.' },
  { cle: 'justesse', label: 'Justesse', unite: '%', aide: 'Écart systématique admis par rapport à une cible de groupe de pairs.' },
  { cle: 'inexactitude', label: 'Inexactitude', unite: '%', aide: 'Écart admis à une valeur de référence externe — c’est le critère de l’évaluation externe de la qualité.' },
  { cle: 'suivi', label: 'Normes de suivi', unite: '', aide: 'Comparaison de techniques — norme de suivi.' },
  { cle: 'interpretation', label: 'Normes d’interprétation', unite: '', aide: 'Comparaison de techniques — norme d’interprétation.' },
];
const NIVEAUX: { cle: string; label: string }[] = [
  { cle: 'Bas', label: 'Bas' }, { cle: 'Moyen', label: 'Moyen' }, { cle: 'Eleve', label: 'Élevé' },
];

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontSize: 12.5 };

/** Les trois grandeurs comparables d'un référentiel à l'autre. Le gabarit est
 *  celui de la SFBC ; RICOS et CLIA y rangent leurs limites à vocabulaire
 *  égal : CV désirable → reproductibilité, biais → justesse, erreur totale →
 *  inexactitude (voir `backend/scripts/data-ricos-clia.js`). */
const GRANDEURS: { cle: string; label: string }[] = [
  { cle: 'reproductibilite', label: 'CV %' },
  { cle: 'justesse', label: 'Biais %' },
  { cle: 'inexactitude', label: 'ET %' },
];

/** Une valeur du référentiel. `null` s'écrit « — » et se distingue à l'œil d'un
 *  zéro : un seuil absent doit rendre l'essai sans verdict, un seuil à zéro
 *  déclarerait non conforme toute méthode. */
function Val({ v }: { v: number | null | undefined }) {
  if (v === null || v === undefined) {
    return <span title="La source ne donne pas cette valeur, ou elle n’a pas pu être lue. Ce n’est pas zéro." style={{ color: 'var(--text-muted)', cursor: 'help' }}>—</span>;
  }
  return <span style={{ color: 'var(--text-strong)' }}>{v}</span>;
}

/** Les trois niveaux d'une grandeur, condensés : une seule valeur quand la
 *  limite est la même à tout niveau (RICOS, CLIA), le triplet « bas / moyen /
 *  élevé » quand la source les distingue (SFBC). Une cellule entièrement vide
 *  porte, s'il existe, le mot de la source en infobulle — c'est là que la CLIA
 *  dit « ± 4 mmol/l », une limite absolue qu'un pourcentage ne peut pas dire. */
function ValNiveaux({ l, cle }: { l: Ligne; cle: string }) {
  const vals = ['Bas', 'Moyen', 'Eleve'].map((n) => l[`${cle}${n}`] as number | null);
  const nonNulles = vals.filter((v) => v !== null && v !== undefined) as number[];
  if (!nonNulles.length) {
    const note = typeof l.notes === 'string' ? l.notes : '';
    return (
      <span title={note || 'La source ne donne pas cette valeur, ou elle n’a pas pu être lue. Ce n’est pas zéro.'}
        style={{ color: note ? 'var(--amber-600)' : 'var(--text-muted)', cursor: 'help' }}>
        {note ? '± *' : '—'}
      </span>
    );
  }
  if (nonNulles.every((v) => v === nonNulles[0]) && nonNulles.length === vals.length) {
    return <span style={{ color: 'var(--text-strong)' }}>{nonNulles[0]}</span>;
  }
  return (
    <span title="Bas / Moyen / Élevé" style={{ color: 'var(--text-strong)', cursor: 'help' }}>
      {vals.map((v) => (v === null || v === undefined ? '—' : String(v))).join(' / ')}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * LES SOURCES DE CRITÈRES (GED-16)
 *
 * ⚠️ CORRECTION D'UN ÉNONCÉ. Le lot désignait `ref-criteres/source` comme « la
 * suppression d'une source du référentiel de critères ». Il n'en existe pas :
 * `routes/refcritere.ts` ne déclare AUCUN verbe DELETE, ni sur les sources, ni
 * sur les lignes. La route dormante est `POST /ged/ref-criteres/source` — une
 * CRÉATION, restée sans formulaire (le script de reprise converti refuse de
 * créer une source et renvoie à un écran qui n'avait pas le champ).
 *
 * ── Et il ne faut pas en ouvrir une ───────────────────────────────────────
 *
 * Une source est ce contre quoi un verdict a été rendu. Le contrôleur le dit
 * déjà à sa façon en refusant de réécrire la VERSION d'une source standard :
 * « les dossiers déjà figés citeraient une version qu'ils n'ont pas utilisée ».
 * Une suppression ferait pire — elle ne laisserait rien à citer. Le geste
 * légitime est donc le RETRAIT DU CHOIX (`actif: false`), qui est un
 * enregistrement comme un autre, journalisé, et qui n'efface rien.
 * ═════════════════════════════════════════════════════════════════════════ */

/** Saisie d'une source et de sa version. Les deux sont obligatoires côté
 *  moteur : une source sans version ne peut pas servir de preuve dans un
 *  dossier figé, et c'est un refus, pas un avertissement. */
function SourceForm({ source, onFait, onAnnule }: {
  source: Source | null; onFait: () => void; onAnnule: () => void;
}) {
  const [f, setF] = useState({
    code: source?.code || '', libelle: source?.libelle || '', version: source?.version || '',
    referenceBiblio: source?.referenceBiblio || '', dateVersion: source?.dateVersion || '',
    commentaire: source?.commentaire || '',
  });
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const maj = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));
  // Une source STANDARD est une table de société savante reprise telle quelle :
  // sa version est verrouillée par le moteur. On le dit ici plutôt que de
  // laisser l'utilisateur saisir puis se faire refuser.
  const versionVerrouillee = !!source?.standard;

  const declarationSrc = React.useMemo(() => [
    { id: 'src-code', label: 'Code de la source', valeur: f.code, requis: true },
    { id: 'src-ver', label: 'Version', valeur: f.version, requis: true },
  ], [f.code, f.version]);
  const { ref: refSource, verifier: verifierSource, Bandeau: BandeauSource } = useFormulaire(declarationSrc);

  const enregistrer = () => run('save', async () => {
    setErreur('');
    if (!verifierSource()) return;
    const r = await fetchEcriture(`/api/ged/ged/ref-criteres/source${source ? `/${source.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(versionVerrouillee ? { ...f, version: undefined } : f),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
    onFait();
  });

  return (
    <div ref={refSource} style={{ ...card, padding: 16, display: 'grid', gap: 12 }}>
      <BandeauSource />
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
          {source ? `Modifier la source — ${source.code}` : 'Enregistrer une source de critères'}
        </div>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '78ch' }}>
          Une source est la table contre laquelle un verdict sera rendu. Elle est portée{' '}
          <strong>avec sa version</strong> : une société savante peut réviser sa table, un dossier
          figé doit continuer à dire contre quoi il a été jugé. Une révision se saisit donc comme
          une <strong>nouvelle</strong> source, jamais en réécrivant celle-ci.
        </p>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        <div><label style={lbl} htmlFor="src-code">Code</label>
          <input id="src-code" style={inputStyle} value={f.code} onChange={(e) => maj('code', e.target.value)} placeholder="SFBC" /></div>
        <div><label style={lbl} htmlFor="src-lib">Intitulé</label>
          <input id="src-lib" style={inputStyle} value={f.libelle} onChange={(e) => maj('libelle', e.target.value)}
            placeholder="Table des limites d’acceptabilité proposées" /></div>
        <div><label style={lbl} htmlFor="src-ver">Version</label>
          <input id="src-ver" style={inputStyle} value={f.version} disabled={versionVerrouillee}
            onChange={(e) => maj('version', e.target.value)} placeholder="2010" />
          {versionVerrouillee && (
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
              Source standard : sa version ne se corrige pas en place. Enregistrez la révision
              comme une nouvelle source.
            </p>
          )}
        </div>
        <div><label style={lbl} htmlFor="src-date">Date de la version</label>
          <input id="src-date" style={inputStyle} value={f.dateVersion} onChange={(e) => maj('dateVersion', e.target.value)} placeholder="2010-06" /></div>
      </div>
      <div><label style={lbl} htmlFor="src-bib">Référence bibliographique</label>
        <input id="src-bib" style={inputStyle} value={f.referenceBiblio} onChange={(e) => maj('referenceBiblio', e.target.value)}
          placeholder="Auteur, titre, revue, année, pages — de quoi la retrouver dix ans plus tard." /></div>
      <div><label style={lbl} htmlFor="src-com">Commentaire</label>
        <textarea id="src-com" rows={2} style={inputStyle} value={f.commentaire} onChange={(e) => maj('commentaire', e.target.value)} /></div>

      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer la source', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

/** Ce qu'emporte le retrait d'une source du choix. Établi sur ce que sert
 *  `GET ged/ref-criteres` : le nombre de lignes rattachées, et rien de deviné. */
function consequencesSource(s: Source, lignes: Ligne[]): Consequence[] {
  const rattachees = lignes.filter((l) => l.sourceId === s.documentId);
  const actives = rattachees.filter((l) => l.actif);
  const c: Consequence[] = [];
  if (actives.length) {
    c.push({
      cle: 'lignes', gravite: 'attention',
      texte: `${actives.length} ligne(s) de critères se rattachent à cette source et RESTENT actives : ${actives.slice(0, 5).map((l) => l.libelle).join(' · ')}${actives.length > 5 ? ` … (+${actives.length - 5})` : ''}.`,
      detail: 'Retirer la source ne retire pas ses seuils. Le moteur choisit la ligne applicable par paramètre, pas par source : ces lignes continueront de rendre des verdicts. Pour cesser de juger contre elles, c’est chaque ligne qu’il faut traiter.',
    });
  }
  c.push({
    cle: 'choix', gravite: 'info',
    texte: 'La source disparaît de la liste proposée à la saisie d’une nouvelle ligne.',
    detail: 'C’est le seul effet direct du retrait — et c’est tout ce qu’il doit être.',
  });
  c.push({
    cle: 'figes', gravite: 'info',
    texte: 'Les dossiers de validation déjà figés ne sont pas affectés.',
    detail: `Ils citent le couple code + version (« ${s.code}, version ${s.version} »), copié dans l’essai au moment du verdict. L’enregistrement reste en base : rien n’est effacé, donc rien ne devient incitable.`,
  });
  c.push({
    cle: 'trace', gravite: 'info',
    texte: 'Le retrait est inscrit au journal chaîné (ReferentielSourceModifiee) et se défait par le même geste.',
  });
  return c;
}

/** Saisie d'une ligne. Toutes les cellules sont éditables, y compris pour être
 *  VIDÉES : corriger une transcription, c'est parfois retirer une valeur qu'on
 *  a cru lire. */
function Formulaire({ ligne, sources, onFait, onAnnule }: {
  ligne: Ligne | null; sources: Source[]; onFait: () => void; onAnnule: () => void;
}) {
  const champs = useMemo(() => {
    const o: Record<string, string> = {
      libelle: String(ligne?.libelle || ''), analyteCode: String(ligne?.analyteCode || ''),
      famille: String(ligne?.famille || 'autre'), unite: String(ligne?.unite || ''),
      domaine: String(ligne?.domaine || ''), valeursUsuelles: String(ligne?.valeursUsuelles || ''),
      sourceId: String(ligne?.sourceId || sources.find((s) => s.actif)?.documentId || ''),
      intervallePct: ligne?.intervallePct === null || ligne?.intervallePct === undefined ? '' : String(ligne.intervallePct),
    };
    for (const n of NIVEAUX) {
      const v = ligne?.[`niveau${n.cle}`];
      o[`niveau${n.cle}`] = v === null || v === undefined ? '' : String(v);
    }
    for (const c of CRITERES) {
      for (const n of NIVEAUX) {
        const v = ligne?.[`${c.cle}${n.cle}`];
        o[`${c.cle}${n.cle}`] = v === null || v === undefined ? '' : String(v);
      }
    }
    return o;
  }, [ligne, sources]);

  const [f, setF] = useState(champs);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const maj = (k: string, v: string) => setF((x) => ({ ...x, [k]: v }));

  const declarationLigne = React.useMemo(() => [
    { id: 'ref-lib', label: 'Paramètre', valeur: f.libelle, requis: true },
    { id: 'ref-src', label: 'Source', valeur: f.sourceId, requis: true },
  ], [f.libelle, f.sourceId]);
  const { ref: refLigne, verifier: verifierLigne, Bandeau: BandeauLigne } = useFormulaire(declarationLigne);

  const enregistrer = () => run('save', async () => {
    setErreur('');
    if (!verifierLigne()) return;
    const r = await fetchEcriture(`/api/ged/ged/ref-criteres/ligne${ligne ? `/${ligne.documentId}` : ''}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f),
    });
    if (!r.ok) { setErreur((await r.json().catch(() => null))?.error?.message || 'Enregistrement refusé.'); return; }
    onFait();
  });

  return (
    <div ref={refLigne} style={{ ...card, padding: 16, display: 'grid', gap: 14 }}>
      <BandeauLigne />
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
        {ligne ? `Modifier — ${ligne.libelle}` : 'Ajouter une ligne au référentiel'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div><label style={lbl} htmlFor="ref-lib">Paramètre</label>
          <input id="ref-lib" style={inputStyle} value={f.libelle} onChange={(e) => maj('libelle', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="ref-code">Code paramètre du laboratoire</label>
          <input id="ref-code" style={inputStyle} value={f.analyteCode} onChange={(e) => maj('analyteCode', e.target.value)} placeholder="GLU" /></div>
        <div><label style={lbl} htmlFor="ref-fam">Famille</label>
          <select id="ref-fam" style={inputStyle} value={f.famille} onChange={(e) => maj('famille', e.target.value)}>
            {Object.entries(FAMILLES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor="ref-src">Source</label>
          {/* Les sources RETIRÉES du choix ne sont plus proposées — mais celle
              que porte déjà la ligne reste offerte, sinon modifier une ligne
              rattachée à une source retirée la déplacerait en silence. */}
          <select id="ref-src" style={inputStyle} value={f.sourceId} onChange={(e) => maj('sourceId', e.target.value)}>
            {sources.filter((s) => s.actif || s.documentId === ligne?.sourceId)
              .map((s) => <option key={s.documentId} value={s.documentId}>{s.code} — {s.version}{s.actif ? '' : ' (retirée du choix)'}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor="ref-un">Unité</label>
          <input id="ref-un" style={inputStyle} value={f.unite} onChange={(e) => maj('unite', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="ref-dom">Domaine de mesure</label>
          <input id="ref-dom" style={inputStyle} value={f.domaine} onChange={(e) => maj('domaine', e.target.value)} placeholder="0,5-50,0" /></div>
        <div><label style={lbl} htmlFor="ref-usu">Valeurs usuelles</label>
          <input id="ref-usu" style={inputStyle} value={f.valeursUsuelles} onChange={(e) => maj('valeursUsuelles', e.target.value)} /></div>
        <div><label style={lbl} htmlFor="ref-int">Intervalle %</label>
          <input id="ref-int" style={inputStyle} value={f.intervallePct} onChange={(e) => maj('intervallePct', e.target.value)} /></div>
      </div>

      <div>
        <span style={lbl}>Concentrations qui définissent les trois niveaux</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {NIVEAUX.map((n) => (
            <div key={n.cle} style={{ flex: 1 }}>
              <input aria-label={`Niveau ${n.label}`} style={inputStyle} value={f[`niveau${n.cle}`]}
                onChange={(e) => maj(`niveau${n.cle}`, e.target.value)} placeholder={n.label} />
            </div>
          ))}
        </div>
        <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          C’est en comparant la cible d’un contrôle interne à ces trois concentrations que l’outil
          <strong> propose</strong> — sans jamais le décider seul — la colonne contre laquelle un
          niveau sera jugé.
        </p>
      </div>

      {CRITERES.map((c) => (
        <div key={c.cle}>
          <span style={lbl}>{c.label} {c.unite && <span style={{ fontWeight: 400, textTransform: 'none' }}>({c.unite})</span>}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            {NIVEAUX.map((n) => (
              <input key={n.cle} aria-label={`${c.label} ${n.label}`} style={inputStyle} value={f[`${c.cle}${n.cle}`]}
                onChange={(e) => maj(`${c.cle}${n.cle}`, e.target.value)} placeholder={n.label} />
            ))}
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>{c.aide}</p>
        </div>
      ))}

      {erreur && <div style={{ fontSize: 12.5, color: 'var(--red-600)' }}>{erreur}</div>}
      <PiedActions taille="sm" annuler={{ onClick: onAnnule }} principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('save'), disabled: estOccupe(), onClick: () => void enregistrer() }} />
    </div>
  );
}

export function Referentiel() {
  const { session, ouvrirPanneau } = useGed();
  const [d, setD] = useState<Dossier | null>(null);
  const [chargement, setChargement] = useState(true);
  const [tour, setTour] = useState(0);
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('tous');
  const [edite, setEdite] = useState<Ligne | null>(null);
  const [saisie, setSaisie] = useState(false);
  const [critere, setCritere] = useState('repetabilite');
  // Le référentiel affiché (SFBC, RICOS, CLIA… ou tous) et la bascule
  // d'affichage : « par critère » (un critère, tous les analytes) ou « par
  // analyte » (un analyte, tous les référentiels côte à côte) — demande de
  // l'utilisatrice du 17/08/2026.
  const [refSel, setRefSel] = useState('tous');
  const [vue, setVue] = useState<'critere' | 'analyte'>('critere');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(vue);
  // Sources : la saisie (création, correction) et le retrait du choix. Trois
  // états distincts plutôt qu'un mode : on ne doit jamais pouvoir avoir à
  // l'écran un formulaire de création ET un panneau de retrait.
  const [sourceSaisie, setSourceSaisie] = useState<Source | null | undefined>(undefined);
  const [sourceRetiree, setSourceRetiree] = useState<Source | null>(null);

  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/ref-criteres', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setD(j); })
      .catch(() => { if (vivant) setD(null); })
      .finally(() => { if (vivant) setChargement(false); });
    return () => { vivant = false; };
  }, [tour]);

  const recharger = () => {
    setSaisie(false); setEdite(null);
    setSourceSaisie(undefined); setSourceRetiree(null);
    setTour((t) => t + 1);
  };

  /** Le retrait d'une source du choix. `actif: false` par la route
   *  d'enregistrement — il n'existe aucun DELETE, et il n'en faut pas. */
  const retirerSource = async (): Promise<string> => {
    if (!sourceRetiree) return 'Aucune source visée.';
    const r = await fetchEcriture(`/api/ged/ged/ref-criteres/source/${sourceRetiree.documentId}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actif: false }),
    });
    if (!r.ok) return (await r.json().catch(() => null))?.error?.message || 'Retrait refusé.';
    recharger();
    return '';
  };

  const remettreSource = async (s: Source) => {
    await fetchEcriture(`/api/ged/ged/ref-criteres/source/${s.documentId}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ actif: true }),
    });
    setTour((t) => t + 1);
  };

  const filtrees = useMemo(() => {
    const base = (d?.lignes || [])
      .filter((l) => (filtre === 'tous' ? true : l.famille === filtre))
      .filter((l) => (refSel === 'tous' ? true : l.sourceCode === refSel));
    const qq = q.trim().toLowerCase();
    return qq ? base.filter((l) => `${l.libelle} ${l.analyteCode || ''}`.toLowerCase().includes(qq)) : base;
  }, [d, filtre, q, refSel]);

  /** Les codes de référentiels réellement présents dans les lignes, dans
   *  l'ordre des sources : ce sont eux qu'on propose au choix et eux qui font
   *  les colonnes de la vue par analyte. */
  const codesReferentiels = useMemo(() => {
    const presents = new Set((d?.lignes || []).map((l) => l.sourceCode).filter(Boolean));
    return (d?.sources || []).map((s) => s.code).filter((c, i, a) => presents.has(c) && a.indexOf(c) === i);
  }, [d]);

  /** Vue par analyte : chaque analyte regroupe ses lignes de TOUS les
   *  référentiels affichés, pour la comparaison d'un coup d'œil. Le
   *  rapprochement se fait par le code analyte du laboratoire — c'est à ça
   *  qu'il sert — et, à défaut, par l'intitulé : deux référentiels n'écrivent
   *  pas « ASAT » de la même façon. */
  const parAnalyte = useMemo(() => {
    const groupes = new Map<string, { libelle: string; analyteCode: string | null; parSource: Map<string, Ligne> }>();
    for (const l of filtrees) {
      const cle = l.analyteCode ? `code:${l.analyteCode}` : `lib:${l.libelle.trim().toLowerCase()}`;
      let g = groupes.get(cle);
      if (!g) { g = { libelle: l.libelle, analyteCode: l.analyteCode, parSource: new Map() }; groupes.set(cle, g); }
      // Première ligne rencontrée par référentiel : le moteur n'en sert
      // normalement qu'une par (source, analyte).
      if (l.sourceCode && !g.parSource.has(l.sourceCode)) g.parSource.set(l.sourceCode, l);
    }
    return [...groupes.values()].sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr'));
  }, [filtrees]);
  const pages = usePagination(filtrees);
  const colonnesComparaison = refSel === 'tous' ? codesReferentiels : codesReferentiels.filter((c) => c === refSel);

  /* `Skeleton` et non une phrase grise (24/08/2026) : l'attente se dit de la
     même façon sur les 32 autres écrans, et le gabarit annonce la FORME de ce
     qui arrive au lieu de laisser la page vide. */
  if (chargement) return <Skeleton rows={5} />;
  if (!d) return null;
  const isRQ = d.peutModifier && session.roles.includes('rq');
  const c = CRITERES.find((x) => x.cle === critere) || CRITERES[0];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow={<>Ressources · <RefISO code="7.3.5" /></>} title="Critères de performance — référentiel" />
      <RegistresLies actif="refperf" />

      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Les limites d’acceptabilité contre lesquelles une méthode est jugée, avec <strong>leur source
        et sa version</strong>. C’est cette version qu’un dossier de validation figera : une société
        savante peut réviser sa table, le dossier doit continuer à dire contre quoi il a été jugé.
      </p>

      {d.compte.lignes === 0 && (
        <EmptyState
          titre="Le référentiel est vide."
          indice="Aucune limite d’acceptabilité n’est enregistrée. Ce n’est pas « aucun critère » : c’est qu’aucun n’a été saisi — et tant qu’il en est ainsi, chaque essai d’un dossier de méthode restera sans verdict."
          action={isRQ ? (
            // Une ligne exige une source : proposer « ajouter une ligne » sans
            // source mènerait à un refus du moteur (« un seuil sans source n’est
            // pas opposable ») après la saisie de soixante cellules.
            d.sources.length === 0
              ? <Bouton variante="primaire" onClick={() => setSourceSaisie(null)}><I n="plus" s={14} />Enregistrer la première source</Bouton>
              : <Bouton variante="primaire" onClick={() => setSaisie(true)}><I n="plus" s={14} />Ajouter la première ligne</Bouton>
          ) : undefined} />
      )}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ ...lbl, marginBottom: 0 }}>Sources des critères</span>
          <span style={{ flex: 1 }} />
          {isRQ && sourceSaisie === undefined && !sourceRetiree && (
            <Bouton variante="primaire" taille="sm" onClick={() => setSourceSaisie(null)}>
              <I n="plus" s={13} />Enregistrer une source
            </Bouton>
          )}
        </div>
        {d.sources.length === 0 && (
          <div style={{ padding: '0 14px 14px' }}>
            <EmptyState
              titre="Aucune source de critères n’est enregistrée."
              indice="Une ligne de référentiel ne peut pas exister sans source : un seuil sans source n’est pas opposable. Commencez par enregistrer la table dont viennent les seuils, avec sa version." />
          </div>
        )}
        {d.sources.map((s) => (
          /* Le clic ouvre la source ; « Retirer du choix » et « Remettre au
             choix » restent — ce ne sont pas le geste par défaut. */
          <div key={s.documentId}
            {...(isRQ && sourceSaisie === undefined && !sourceRetiree
              ? ligneOuvrable(() => setSourceSaisie(s), `Modifier ${s.code}`,
                { padding: '11px 14px', borderTop: '1px solid var(--border-subtle)', opacity: s.actif ? 1 : 0.6 })
              : { style: { padding: '11px 14px', borderTop: '1px solid var(--border-subtle)', opacity: s.actif ? 1 : 0.6 } })}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ ...mono, fontWeight: 700, color: 'var(--color-primary-active)' }}>{s.code}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{s.libelle}</span>
              <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>
                version {s.version}
              </span>
              {!s.actif && (
                <span title="Retirée du choix : elle n’est plus proposée à la saisie d’une nouvelle ligne. Ses lignes existantes restent actives et continuent de rendre des verdicts."
                  style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--amber-50)', color: 'var(--amber-600)', cursor: 'help' }}>
                  retirée du choix
                </span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{s.nbLignes} paramètre{s.nbLignes > 1 ? 's' : ''}</span>
              {isRQ && sourceSaisie === undefined && !sourceRetiree && (
                <SansOuvrir>
                  {s.actif ? (
                    <Bouton taille="sm" onClick={() => setSourceRetiree(s)}>
                      Retirer du choix
                    </Bouton>
                  ) : (
                    <Bouton taille="sm" onClick={() => void remettreSource(s)}>
                      <I n="refresh" s={12} />Remettre au choix
                    </Bouton>
                  )}
                </SansOuvrir>
              )}
            </div>
            {s.referenceBiblio && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{s.referenceBiblio}</div>
            )}
            {s.commentaire && (
              <div style={{ fontSize: 12.5, color: 'var(--text-body)', marginTop: 4, lineHeight: 1.5 }}>{s.commentaire}</div>
            )}
          </div>
        ))}
      </div>

      {sourceSaisie !== undefined && isRQ && (
        <SourceForm source={sourceSaisie} onFait={recharger} onAnnule={() => setSourceSaisie(undefined)} />
      )}

      {sourceRetiree && isRQ && (
        <SuppressionRaisonnee
          nature="retrait"
          titre="Retirer une source du choix"
          objet={<><strong>{sourceRetiree.code}</strong> — {sourceRetiree.libelle}, version {sourceRetiree.version}.</>}
          consequences={consequencesSource(sourceRetiree, d.lignes)}
          libelleAction="Retirer du choix"
          onConfirmer={retirerSource}
          onAnnuler={() => setSourceRetiree(null)}
          note={<>
            <strong>Il n’existe aucune suppression de source, et il n’en faut pas.</strong> Une source
            est ce contre quoi un verdict a été rendu : l’effacer laisserait des dossiers figés citant
            une référence introuvable. Le moteur refuse déjà, pour la même raison, de réécrire la
            version d’une source standard.
          </>}
        />
      )}

      {d.compte.cellulesVides > 0 && (
        <div style={{ background: 'var(--amber-50)', borderInlineStart: '3px solid var(--amber-500)', borderRadius: 'var(--radius-sm)', padding: '9px 13px', fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {d.compte.cellulesVides} valeur{d.compte.cellulesVides > 1 ? 's' : ''} non renseignée{d.compte.cellulesVides > 1 ? 's' : ''}
          </strong>{' '}
          sur {d.compte.cellulesTotal}. Elles s’affichent « — » et non « 0 » : un seuil absent rend
          l’essai <em>sans verdict</em>, il ne le rend pas conforme. Complétez-les depuis la table
          d’origine plutôt que de les estimer.
        </div>
      )}

      {(saisie || edite) && isRQ && (
        <Formulaire ligne={edite} sources={d.sources} onFait={recharger}
          onAnnule={() => { setSaisie(false); setEdite(null); }} />
      )}

      {d.compte.lignes > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <FilterBar
                q={q} onQ={setQ} placeholder="Rechercher un paramètre…"
                chips={[
                  { k: 'tous', label: 'Tous', n: d.compte.lignes },
                  ...d.compte.parFamille.map((f) => ({ k: f.famille, label: FAMILLES[f.famille] || f.famille, n: f.n })),
                ]}
                actif={filtre} onChip={setFiltre}
                total={d.compte.lignes} affiches={filtrees.length}
                onReset={() => { setQ(''); setFiltre('tous'); }} />
            </div>
            {isRQ && !saisie && !edite && (
              <Bouton variante="primaire" taille="sm" onClick={() => setSaisie(true)}>
                <I n="plus" s={13} />Ajouter
              </Bouton>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Référentiel :</span>
            <Segment ariaLabel="Référentiel" valeur={refSel} onChange={setRefSel}
              options={[{ valeur: 'tous', libelle: 'Tous' }, ...codesReferentiels.map((code) => ({ valeur: code, libelle: code }))]} />
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Affichage :</span>
            <Segment ariaLabel="Affichage" valeur={vue} onChange={setVue}
              options={[
                { valeur: 'critere' as const, libelle: 'Par critère' },
                { valeur: 'analyte' as const, libelle: 'Par paramètre', aide: 'Un paramètre par ligne, tous ses référentiels côte à côte : CV, biais et erreur totale se comparent d’un coup d’œil.' },
              ]} />
          </div>

          {vue === 'critere' && (
            <>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Critère affiché :</span>
                <Segment ariaLabel="Critère" valeur={critere} onChange={setCritere} options={CRITERES.map((x) => ({ valeur: x.cle, libelle: x.label }))} />
              </div>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{c.aide}</p>
            </>
          )}

          {vue === 'analyte' && (
            <>
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5, maxWidth: '92ch' }}>
                Chaque analyte regroupe les critères de <strong>tous les référentiels</strong> affichés,
                à vocabulaire égal : CV (reproductibilité), biais (justesse) et ET (erreur totale —
                inexactitude). Trois valeurs séparées de « / » suivent les niveaux Bas / Moyen / Élevé ;
                une valeur seule vaut à tout niveau. « ± * » signale une limite <strong>absolue</strong>,
                en unité et non en pourcentage — elle se lit en infobulle. Ces tables se{' '}
                <strong>consultent</strong> : aucune n’engage le laboratoire tant qu’une exigence n’est
                pas explicitement retenue dans un dossier.
              </p>
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                      <th scope="col" style={{ textAlign: 'start', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0 }}>Paramètre</th>
                      {colonnesComparaison.map((code) => (
                        <th scope="col" key={code} colSpan={GRANDEURS.length}
                          style={{ textAlign: 'center', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0, borderInlineStart: '1px solid var(--border-subtle)' }}>
                          {code}
                        </th>
                      ))}
                    </tr>
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <th scope="col" />
                      {colonnesComparaison.map((code) => GRANDEURS.map((g, gi) => (
                        <th scope="col" key={`${code}-${g.cle}`}
                          style={{ textAlign: 'end', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-entete-police)', fontWeight: 400, color: 'var(--text-muted)', borderInlineStart: gi === 0 ? '1px solid var(--border-subtle)' : undefined }}>
                          {g.label}
                        </th>
                      )))}
                    </tr>
                  </thead>
                  <tbody>
                    {parAnalyte.map((a, i) => (
                      <tr key={a.analyteCode || a.libelle} style={{ borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                        <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                          {/* Le paramètre s'OUVRE, sans quitter la comparaison
                              qu'on est en train de lire — c'est tout l'objet du
                              panneau superposé. Une ligne SANS code n'est pas
                              cliquable : elle ne désigne aucun paramètre du
                              catalogue, et un bouton qui n'ouvre rien se lit
                              comme une panne. */}
                          {a.analyteCode ? (
                            <button
                              type="button"
                              onClick={() => ouvrirPanneau({
                                type: 'parametre', valeur: a.analyteCode as string,
                                origine: 'Critères de performance',
                              })}
                              title={`Ouvrir la fiche du paramètre ${a.analyteCode}`}
                              style={{
                                background: 'none', border: 'none', padding: 0, textAlign: 'start',
                                cursor: 'pointer', font: 'inherit', display: 'inline-flex',
                                alignItems: 'baseline', gap: 7,
                              }}
                              className="lien-doux">
                              <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{a.libelle}</span>
                              <span style={{ ...mono, color: 'var(--color-primary-active)' }}>{a.analyteCode}</span>
                            </button>
                          ) : (
                            <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{a.libelle}</span>
                          )}
                        </td>
                        {colonnesComparaison.map((code) => {
                          const l = a.parSource.get(code);
                          return GRANDEURS.map((g, gi) => (
                            <td key={`${code}-${g.cle}`}
                              style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'end', ...mono, borderInlineStart: gi === 0 ? '1px solid var(--border-subtle)' : undefined }}>
                              {l
                                ? <ValNiveaux l={l} cle={g.cle} />
                                : <span title="Ce référentiel ne couvre pas ce paramètre." style={{ color: 'var(--text-muted)', cursor: 'help' }}>·</span>}
                            </td>
                          ));
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parAnalyte.length === 0 && (
                  <div style={{ padding: 16 }}>
                    <EmptyState titre="Aucun paramètre ne correspond à ces critères."
                      action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); setRefSel('tous'); }}>Réinitialiser</Bouton>} />
                  </div>
                )}
              </div>
            </>
          )}

          {vue === 'critere' && (
          <>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table className="ged-tableau" ref={enTeteCollant} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                  <th scope="col" style={{ textAlign: 'start', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0 }}>Paramètre</th>
                  <th scope="col" style={{ textAlign: 'start', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0 }}>Unité</th>
                  <th scope="col" style={{ textAlign: 'end', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0 }} colSpan={3}>
                    Concentration des niveaux
                  </th>
                  <th scope="col" style={{ textAlign: 'end', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', ...lbl, marginBottom: 0 }} colSpan={3}>
                    {c.label} {c.unite && `(${c.unite})`}
                  </th>
                </tr>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <th scope="col" /><th scope="col" />
                  {NIVEAUX.map((n) => <th scope="col" key={`c${n.cle}`} style={{ textAlign: 'end', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-entete-police)', fontWeight: 400, color: 'var(--text-muted)' }}>{n.label}</th>)}
                  {NIVEAUX.map((n) => <th scope="col" key={`v${n.cle}`} style={{ textAlign: 'end', padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', fontSize: 'var(--tableau-entete-police)', fontWeight: 400, color: 'var(--text-muted)' }}>{n.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {pages.visibles.map((l, i) => (
                  /* Le clic sur la ligne ouvre la saisie : la colonne d'actions
                     ne portait qu'un « Modifier » par paramètre, répété sur
                     toute la hauteur du tableau pour le geste qu'on attend
                     déjà d'un clic (Ines, 26/08/2026). */
                  <tr key={l.documentId}
                    {...(isRQ
                      ? ligneOuvrable(() => { setSaisie(false); setEdite(l); }, `Modifier ${l.libelle}`,
                        { borderTop: i ? '1px solid var(--border-subtle)' : 'none' })
                      : { style: { borderTop: i ? '1px solid var(--border-subtle)' : 'none' } })}>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)' }}>
                      <span style={{ fontWeight: 700, color: 'var(--text-strong)' }}>{l.libelle}</span>
                      {l.analyteCode
                        ? <span style={{ ...mono, marginInlineStart: 7, color: 'var(--color-primary-active)' }}>{l.analyteCode}</span>
                        : <span title="Cette ligne n’est rattachée à aucun paramètre du laboratoire : un dossier de méthode ne la trouvera pas automatiquement." style={{ marginInlineStart: 7, fontSize: 12.5, color: 'var(--amber-600)', cursor: 'help' }}>non rattaché</span>}
                      {/* Plusieurs référentiels mêlés : sans ce badge, deux
                          lignes « Glucose » se suivraient sans dire laquelle
                          est la SFBC et laquelle est la CLIA. */}
                      {refSel === 'tous' && codesReferentiels.length > 1 && l.sourceCode && (
                        <span style={{ marginInlineStart: 7, fontSize: 10, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '1px 7px', background: 'var(--cyan-50)', color: 'var(--cyan-700)' }}>
                          {l.sourceCode}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', color: 'var(--text-muted)' }}>{l.unite || '—'}</td>
                    {NIVEAUX.map((n) => (
                      <td key={`c${n.cle}`} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'end', ...mono }}>
                        <Val v={l[`niveau${n.cle}`] as number | null} />
                      </td>
                    ))}
                    {NIVEAUX.map((n) => (
                      <td key={`v${n.cle}`} style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', textAlign: 'end', ...mono, fontWeight: 700 }}>
                        <Val v={l[`${critere}${n.cle}`] as number | null} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {filtrees.length === 0 && (
              <div style={{ padding: 16 }}>
                <EmptyState titre="Aucun paramètre ne correspond à ces critères."
                  action={<Bouton taille="sm" onClick={() => { setQ(''); setFiltre('tous'); setRefSel('tous'); }}>Réinitialiser</Bouton>} />
              </div>
            )}
          </div>
          <Pagination page={pages.page} pages={pages.pages} debut={pages.debut} fin={pages.fin}
            total={pages.total} onPage={pages.setPage} quoi="paramètres" />
          </>
          )}
        </>
      )}
    </div>
  );
}
